import React from 'react'
import SmartLink from '../SmartLink'
import Text from '../Text'
import styles from './styles.module.css'
import sendEvent from '../../utils/analytics'
import { ctaHiddenSuffix, ctaLinkName } from '../../utils/ctaAccessibleName'

// Compose the base class with the prop-driven color/background/border variant,
// matching the former styled-component ternary precedence:
//   dark && primary -> darkPrimary ; dark -> dark ; primary -> primary ; else base
const cx = (base, { primary, dark, fullHeight }) => {
  const classes = [base]
  if (dark && primary) {
    classes.push(styles.darkPrimary)
  } else if (dark) {
    classes.push(styles.dark)
  } else if (primary) {
    classes.push(styles.primary)
  }
  if (fullHeight) {
    classes.push(styles.fullHeight)
  }
  return classes.join(' ')
}

function Cta(props) {
  const { title, textType, route, link, target, onClick, primary, dark, type, disabled, fullHeight, event, hiddenSuffix } = props

  // Descriptive link text (Audit C M3): generic CTA verbs ("Learn more", "Buy")
  // are non-descriptive on their own — Lighthouse's link-text audit flags them and
  // repeated identical anchors are indistinguishable to screen-reader users. Port
  // the CtaV2 (#154) pattern: keep the terse VISIBLE verb and append a
  // visually-hidden (`sr-only`) continuation naming the destination, so the
  // rendered innerText/accessible name is descriptive while the design is
  // unchanged. An explicit `hiddenSuffix` prop wins; otherwise it is derived from
  // the CTA's own route slug / link URL (undefined => nothing appended).
  const suffix = hiddenSuffix !== undefined
    ? hiddenSuffix
    : ctaHiddenSuffix(title, ctaLinkName({ route, link }))
  const label = <>{title}{suffix ? <span className="sr-only"> {suffix}</span> : null}</>

  if (route && route.slug && route.slug.current) {
    const path = route._type === 'story' ? '/stories/' : '/'
    return (
      <SmartLink
        href={`${path}${route.slug.current}`}
        className={cx(styles.linkElement, { primary, dark, fullHeight })}
        onClick={() => sendEvent(event)}
      >
        <Text type={textType || 'regular'}>{label}</Text>
      </SmartLink>
    )
  }

  if (link) {
    return (
      <a className={cx(styles.linkElement, { primary, dark, fullHeight })} href={link} target={target || '_self'} onClick={() => sendEvent(event)}>
        <Text type={textType || 'regular'}>{label}</Text>
      </a>
    )
  }

  if (onClick || type) {
    return (
      <button className={cx(styles.buttonElement, { primary, dark, fullHeight })} onClick={() => onClick() && sendEvent(event)} type={type} disabled={disabled}>
        <Text type={textType || 'regular'}>{label}</Text>
      </button>
    )
  }

  // No route, no link, no onClick/type: the old fallback rendered an <a> with NO
  // href — a non-link that is not keyboard-focusable and exposes no role. Its only
  // behaviour is the analytics ping, i.e. it is an ACTION, so it is now a real
  // <button> (Audit C M3). Class stays `linkElement` so the box is pixel-identical.
  return (
    <button type="button" className={cx(styles.linkElement, { primary, dark, fullHeight })} onClick={() => sendEvent(event)}>
      <Text type={textType || 'regular'}>{label}</Text>
    </button>
  )
}

export default Cta
